/**
 * Recognises a backend reply that means "you are not authenticated" so the API
 * client can refresh the Firebase token and retry.
 *
 * The 400 case is not a guess. `rp-integrations` controllers gate on the
 * Firestore user before anything else and answer a failed/absent Firebase token
 * with `400 ERR_MISSING_USER_ID` ("User ID is required.") instead of a 401.
 * Without this, the client's refresh-and-retry never runs and the user is left
 * staring at that message until they reload — see the Pay Operations amendment
 * queue, which renders the raw backend message in an inline alert.
 *
 * The backend now also returns a proper 401 for this case; the 400 branch stays
 * so the portal keeps recovering against environments running the older build.
 */

/** rp-integrations' code for an unauthenticated request. */
const MISSING_USER_ID_CODE = 'ERR_MISSING_USER_ID';

interface AuthFailureShape {
  response?: {
    status?: number;
    data?: unknown;
  };
}

export function isAuthFailureResponse(error: AuthFailureShape): boolean {
  const status = error?.response?.status;

  if (status === 401) return true;

  // 403 is deliberately excluded: the caller authenticated fine and simply
  // lacks the role. Refreshing the token would not change the outcome.
  if (status !== 400) return false;

  const data = error?.response?.data;
  if (typeof data !== 'object' || data === null) return false;

  return (data as { code?: unknown }).code === MISSING_USER_ID_CODE;
}
